/**
 * @file useTaskAssignees.ts
 * @description Кого можно назначить исполнителем (phase19.31, crm.md T2):
 * `GET /admin/crm/task-assignees` — сотрудники магазина с правом CRM,
 * текущий сотрудник первым. Опции для `SelectField`: «Анна К. (вы) ·
 * Менеджер».
 */

import { useEffect, useMemo, useState } from "react";

import type { SelectFieldOption } from "../../ui/SelectField";
import { listTaskAssignees, type TaskAssigneeOptionDto } from "../../../lib/api-crm";

export interface TaskAssigneesState {
  assignees: TaskAssigneeOptionDto[];
  options: SelectFieldOption[];
  loading: boolean;
  /** Список не загрузился — поле выбора недоступно. */
  failed: boolean;
}

/** Подпись сотрудника в списке выбора. */
export function assigneeOptionLabel(a: TaskAssigneeOptionDto): string {
  const name = a.isMe ? `${a.name} (вы)` : a.name;
  return a.roleName ? `${name} · ${a.roleName}` : name;
}

export function useTaskAssignees(): TaskAssigneesState {
  const [assignees, setAssignees] = useState<TaskAssigneeOptionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listTaskAssignees()
      .then((list) => {
        if (!cancelled) setAssignees(list);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () => assignees.map((a) => ({ value: a.id, label: assigneeOptionLabel(a) })),
    [assignees],
  );

  return { assignees, options, loading, failed };
}
